/*
 * Splide wrappers for the Vanamati theme.
 *
 *   <splide-carousel> — generic carousel (announcement bar today, related products later).
 *   <splide-gallery>  — PDP media gallery: main slider + optional synced thumbnail nav,
 *                       and jumps to a variant's featured image via the variantChange event.
 *
 * Relies on the global `Splide` (assets/splide.min.js) loaded before this file.
 */
(function () {
  /*
   * Autoplay on a plain timer. Splide's own autoplay measures its interval with
   * requestAnimationFrame, so every autoplaying carousel ran a callback on every
   * frame, forever — even scrolled out of view or in a background tab. With
   * three on the homepage that was the largest block of main-thread work the
   * theme itself caused. This keeps Splide's behaviour (pause on hover/focus,
   * restart after any move, hold while dragging, nothing under reduced motion,
   * aria-live "off" while playing) and also rests while the carousel is off
   * screen or the tab is hidden. Pass the Splide instance mounted with
   * autoplay: false. Returns { play, pause, destroy }, or null when it won't run.
   */
  window.vaAutoplay = function (splide, root, interval, opts) {
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return null;
    const waitForLoad = !!(opts && opts.waitForLoad);
    const state = {
      stopped: false,
      hovered: false,
      focused: false,
      dragging: false,
      visible: !('IntersectionObserver' in window),
      loaded: !waitForLoad || document.readyState === 'complete',
    };
    let timer = null;
    const live = splide.Components.Live;

    function canRun() {
      return (
        !state.stopped &&
        !state.hovered &&
        !state.focused &&
        !state.dragging &&
        state.visible &&
        state.loaded &&
        !document.hidden &&
        splide.Components.Slides.isEnough()
      );
    }

    function schedule() {
      clearTimeout(timer);
      timer = null;
      const running = canRun();
      if (live) live.disable(running);
      if (running) timer = setTimeout(() => splide.go('>'), interval);
    }

    function set(key, value) {
      state[key] = value;
      schedule();
    }

    const onEnter = () => set('hovered', true);
    const onLeave = () => set('hovered', false);
    const onFocusIn = () => set('focused', true);
    const onFocusOut = () => set('focused', false);
    const onVisibility = () => schedule();
    const onLoad = () => set('loaded', true);

    root.addEventListener('mouseenter', onEnter);
    root.addEventListener('mouseleave', onLeave);
    root.addEventListener('focusin', onFocusIn);
    root.addEventListener('focusout', onFocusOut);
    document.addEventListener('visibilitychange', onVisibility);
    if (!state.loaded) window.addEventListener('load', onLoad, { once: true });

    // Any move (autoplay's own, an arrow, a dot, a swipe) starts the wait afresh.
    splide.on('move', schedule);
    splide.on('drag', () => set('dragging', true));
    splide.on('dragged', () => set('dragging', false));

    let io = null;
    if (!state.visible) {
      io = new IntersectionObserver((entries) => set('visible', entries[0].isIntersecting));
      io.observe(root);
    }

    schedule();

    return {
      play: () => set('stopped', false),
      pause: () => set('stopped', true),
      destroy() {
        clearTimeout(timer);
        if (io) io.disconnect();
        root.removeEventListener('mouseenter', onEnter);
        root.removeEventListener('mouseleave', onLeave);
        root.removeEventListener('focusin', onFocusIn);
        root.removeEventListener('focusout', onFocusOut);
        document.removeEventListener('visibilitychange', onVisibility);
        window.removeEventListener('load', onLoad);
      },
    };
  };

  if (typeof window.Splide === 'undefined') return;

  function boolAttr(el, name, fallback) {
    const value = el.getAttribute(name);
    if (value === null) return fallback;
    return value === 'true' || value === '';
  }

  function intAttr(el, name, fallback) {
    const value = parseInt(el.getAttribute(name), 10);
    return Number.isNaN(value) ? fallback : value;
  }

  function floatAttr(el, name, fallback) {
    const value = parseFloat(el.getAttribute(name));
    return Number.isNaN(value) ? fallback : value;
  }

  if (!customElements.get('splide-carousel')) {
    customElements.define(
      'splide-carousel',
      class SplideCarousel extends HTMLElement {
        connectedCallback() {
          if (this.splide) return;
          const root = this.querySelector('.splide') || this;
          const type = this.dataset.type || 'slide';

          const autoWidth = boolAttr(this, 'data-auto-width', false);

          const options = {
            type: type,
            perMove: 1,
            gap: this.dataset.gap || '0',
            arrows: boolAttr(this, 'data-arrows', true),
            pagination: boolAttr(this, 'data-pagination', false),
            drag: true,
            rewind: type !== 'loop',
            speed: intAttr(this, 'data-speed', 600),
          };

          if (autoWidth) {
            // Slide widths come from CSS — Splide measures them, so its drag bounds always
            // match the real layout. This keeps touch dragging reliable on mobile (no
            // perPage-vs-width mismatch that breaks the swipe).
            options.autoWidth = true;
            // Stop the last slide flush with the right edge instead of leaving empty
            // trailing space / over-scroll past the content.
            options.omitEnd = true;
          } else {
            options.perPage = floatAttr(this, 'data-per-page', 1);

            // Responsive per-page: desktop-sm (<=1300) / tablet (<=990) / mobile (<=749).
            const perDesktopSm = this.getAttribute('data-per-page-desktop-sm');
            const perTablet = this.getAttribute('data-per-page-tablet');
            const perMobile = this.getAttribute('data-per-page-mobile');
            if (perDesktopSm || perTablet || perMobile) {
              options.breakpoints = {};
              if (perDesktopSm) options.breakpoints[1300] = { perPage: parseFloat(perDesktopSm) };
              if (perTablet) options.breakpoints[990] = { perPage: parseFloat(perTablet) };
              if (perMobile) options.breakpoints[749] = { perPage: parseFloat(perMobile) };
            }

            if (type === 'loop') {
              // Splide's loop type doesn't support fractional perPage — the clone math
              // breaks and the slider snaps back to the first slide mid-autoplay (seen
              // on origin-story mobile). Floor to an integer; use data-peek for the
              // partial-next-slide look instead.
              const wholePage = (v) => Math.max(1, Math.floor(v));
              options.perPage = wholePage(options.perPage);
              if (options.breakpoints) {
                Object.values(options.breakpoints).forEach((bp) => {
                  bp.perPage = wholePage(bp.perPage);
                });
              }
            }
          }

          // Right "peek" so the next slide is partially visible.
          const peek = this.getAttribute('data-peek');
          if (peek) options.padding = { right: peek };

          this.splide = new Splide(root, options).mount();

          if (boolAttr(this, 'data-autoplay', false)) {
            this.autoplay = window.vaAutoplay(this.splide, root, intAttr(this, 'data-interval', 5000));
          }

          // Images held back in data-src / data-srcset (e.g. origin-story) load
          // once the carousel is within 600px of the viewport. Queried after
          // mount so Splide's loop clones are included.
          if (this.querySelector('img[data-src]')) this.revealImagesWhenNear();

          // External arrows: prev/next buttons placed outside `.splide` (e.g. a header row),
          // bound manually since Splide only auto-binds arrows inside its root.
          this.bindExternalArrows();
        }

        revealImagesWhenNear() {
          const reveal = () => {
            this.querySelectorAll('img[data-src]').forEach((img) => {
              if (img.dataset.srcset) {
                img.srcset = img.dataset.srcset;
                img.removeAttribute('data-srcset');
              }
              img.src = img.dataset.src;
              img.removeAttribute('data-src');
            });
          };
          if (!('IntersectionObserver' in window)) return reveal();
          this.revealObserver = new IntersectionObserver(
            (entries) => {
              if (!entries[0].isIntersecting) return;
              this.revealObserver.disconnect();
              reveal();
            },
            { rootMargin: '600px 0px' }
          );
          this.revealObserver.observe(this);
        }

        bindExternalArrows() {
          const sp = this.splide;
          const prev = this.querySelector('[data-splide-arrow="prev"]');
          const next = this.querySelector('[data-splide-arrow="next"]');
          if (!prev && !next) return;

          const loop = (this.dataset.type || 'slide') === 'loop';
          const update = () => {
            const end = sp.Components.Controller.getEnd();
            const i = sp.index;
            if (prev) prev.disabled = !loop && i <= 0;
            if (next) next.disabled = !loop && i >= end;
          };
          if (prev) prev.addEventListener('click', () => sp.go('<'));
          if (next) next.addEventListener('click', () => sp.go('>'));
          sp.on('mounted move moved updated refresh', update);
          update();
        }

        disconnectedCallback() {
          if (this.revealObserver) this.revealObserver.disconnect();
          if (this.autoplay) this.autoplay.destroy();
          if (this.splide) this.splide.destroy();
        }
      }
    );
  }

  if (!customElements.get('splide-gallery')) {
    customElements.define(
      'splide-gallery',
      class SplideGallery extends HTMLElement {
        connectedCallback() {
          if (this.mainSplide) return;

          this.mainEl = this.querySelector('[data-gallery-main]');
          if (!this.mainEl) return;
          this.thumbsEl = this.querySelector('[data-gallery-thumbs]');
          this.mainList = this.mainEl.querySelector('.splide__list');
          this.thumbList = this.thumbsEl ? this.thumbsEl.querySelector('.splide__list') : null;
          this.section = this.dataset.section;

          // Master slide lists in original order — variant filtering re-populates the
          // live <ul>s from these, so hidden slides are kept alive off-DOM.
          this.allMainSlides = Array.from(this.mainList.children);
          this.allThumbSlides = this.thumbList ? Array.from(this.thumbList.children) : [];
          this.visibleKey = null;

          this.filterToVariant(
            this.getAttribute('data-initial-variant-id'),
            this.getAttribute('data-initial-media-id')
          );

          // Re-filter + jump on variant change (product-info's event).
          // NB: PUB_SUB_EVENTS is a `const` global (constants.js), NOT a window
          // property — reference it as a bareword, not window.PUB_SUB_EVENTS.
          if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
              if (!event || !event.data || event.data.sectionId !== this.section) return;
              const variant = event.data.variant;
              if (!variant) return;
              const media = variant.featured_media;
              this.filterToVariant(String(variant.id), media ? String(media.id) : null);
            });
          }
        }

        // A slide stays visible when it's a common image (no data-variant-ids) or the
        // variant is one of its owners (set in va-main-product.liquid from featured media).
        slideMatches(slide, variantId) {
          const owners = (slide.dataset.variantIds || '').split(',').filter(Boolean);
          return owners.length === 0 || owners.indexOf(variantId) > -1;
        }

        /* Show the selected variant's own image(s) plus common images; hide images that
           belong to other variants. A variant with no image of its own keeps the full
           gallery and doesn't move. */
        filterToVariant(variantId, featuredMediaId) {
          const showAll = !variantId || !featuredMediaId;
          const visibleMain = showAll
            ? this.allMainSlides
            : this.allMainSlides.filter((slide) => this.slideMatches(slide, variantId));
          const visibleThumbs = showAll
            ? this.allThumbSlides
            : this.allThumbSlides.filter((slide) => this.slideMatches(slide, variantId));

          const target = featuredMediaId ? `${this.section}-${featuredMediaId}` : null;
          let start = target ? visibleMain.findIndex((slide) => slide.dataset.mediaId === target) : -1;
          if (start < 0) start = 0;

          const key = visibleMain.map((slide) => slide.dataset.mediaId).join('|');
          if (key === this.visibleKey && this.mainSplide) {
            // Same slide set (e.g. variants sharing an image) — just move to it.
            if (target) this.mainSplide.go(start);
            return;
          }
          this.visibleKey = key;

          if (this.mainSplide) {
            this.mainSplide.destroy();
            this.mainSplide = null;
          }
          if (this.thumbSplide) {
            this.thumbSplide.destroy();
            this.thumbSplide = null;
          }

          this.renderList(this.mainList, visibleMain);
          if (this.thumbList) this.renderList(this.thumbList, visibleThumbs);
          this.mountSplides(start);
        }

        renderList(list, slides) {
          while (list.firstChild) list.removeChild(list.firstChild);
          slides.forEach((slide) => list.appendChild(slide));
        }

        mountSplides(start) {
          const loop = boolAttr(this, 'data-loop', true);

          // Pre-mount CSS shows only the first slide at full width; lift that
          // in the same task as mount so no intermediate frame is painted.
          this.classList.add('is-initialized');

          this.mainSplide = new Splide(this.mainEl, {
            type: loop ? 'loop' : 'slide',
            perPage: 1,
            perMove: 1,
            start: start,
            arrows: boolAttr(this, 'data-arrows', true),
            pagination: boolAttr(this, 'data-pagination', true),
            drag: true,
            rewind: !loop,
            autoplay: boolAttr(this, 'data-autoplay', false),
            interval: intAttr(this, 'data-interval', 5000),
            speed: 500,
            pauseOnHover: true,
            pauseOnFocus: true,
            // Phones: the next photo peeks in at the right edge, so it reads as
            // swipeable. Keep 3rem in step with the pre-mount width in va-main-product.
            breakpoints: {
              749: { padding: { right: '3rem' }, gap: '0.4rem' },
            },
          });

          // A tap on the peeking photo moves to it.
          this.mainSplide.on('click', (slide) => {
            if (slide.slide.classList.contains('is-active')) return;
            const ahead = slide.slide.getBoundingClientRect().left > this.mainEl.getBoundingClientRect().left;
            this.mainSplide.go(ahead ? '>' : '<');
          });

          if (this.thumbList && this.thumbList.children.length) {
            this.thumbSplide = new Splide(this.thumbsEl, {
              isNavigation: true,
              rewind: true,
              pagination: false,
              arrows: false,
              gap: '0.8rem',
              fixedWidth: '6.4rem',
              fixedHeight: '6.4rem',
              focus: 0,
              start: start,
              dragMinThreshold: 10,
              breakpoints: {
                749: { fixedWidth: '5.2rem', fixedHeight: '5.2rem' },
              },
            });
            this.mainSplide.sync(this.thumbSplide);
            this.mainSplide.mount();
            this.thumbSplide.mount();
          } else {
            this.mainSplide.mount();
          }
        }

        disconnectedCallback() {
          if (this.unsubscribe) this.unsubscribe();
          if (this.mainSplide) this.mainSplide.destroy();
          if (this.thumbSplide) this.thumbSplide.destroy();
        }
      }
    );
  }
})();
